import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useRequestData, usePrefs, useLabels } from 'wsm-common-data';
import { setClassNames } from '@ddc/classnames-js';
import { trackEvent } from 'ddc-track-event';
import { PriceToggle } from 'wsm-pricing-display';
import { Collapse } from 'react-bootstrap';
import { isValidFuelType } from 'wsm-highlighted-attribute';
import {
	selectIsGridLayout,
	selectIsListLayout,
	selectIsTabbedLayout
} from '../../../utilities/layout';
import { pricingMap } from '../../../utilities/pricingMap';
import {
	publishVehicleData,
	getVehicleObj
} from '../../../utilities/vehicleTracking';
import ConditionalWrapper from '../../ConditionalWrapper';
import WebIntegrationPlaceholder from '../../WebIntegrationPlaceholder';
import { supplementalTitleShape } from '../../../propTypes/vehiclePropType';

/**
 * If a string is entirely uppercase letters (ignoring non-letter characters),
 * convert it to title case. Mixed-case strings are returned unchanged so that
 * values like "Plug-In Hybrid" are preserved.
 *
 * @param {string} str
 * @returns {string}
 */
export const titleCaseIfAllCaps = (str) => {
	if (!str) return str;
	// Check if every letter in the string is uppercase
	const letters = str.replace(/[^a-zA-Z]/g, '');
	if (letters.length === 0) return str;
	if (letters !== letters.toUpperCase()) return str;
	return str.toLowerCase().replace(/\b[a-z]/g, (char) => char.toUpperCase());
};

/**
 *
 * @param {Object} props
 * @returns
 */
const VehicleTitle = ({
	title,
	supplementalTitle,
	link,
	vehicleId,
	vin,
	condition,
	certified,
	pricing,
	accountInfo,
	isDisplayingEPrice,
	showExpandedPricing,
	setShowExpandedPricing,
	showNewCarBoostPaymentsInTitle
}) => {
	const { windowId, widgetName } = useRequestData();
	const labels = useLabels();
	const {
		showInformationTabs,
		vehicleTitleFontSize,
		finalPriceSize,
		showVINAboveTitle
	} = usePrefs();
	const isTabbedLayout = useSelector((state) => selectIsTabbedLayout(state));
	const isGridLayout = useSelector((state) => selectIsGridLayout(state));
	const isListLayout = useSelector((state) => selectIsListLayout(state));
	// TODO: remove mapping here (and in ws-detailed-pricing) and update wsm-pricing-display to use lowercase version once GVM databus is retired
	const componentPricing = pricingMap(pricing);

	// Normalize title to an array to prevent TypeError when title is undefined/null/string
	let normalizedTitle = [];
	if (Array.isArray(title)) {
		normalizedTitle = title;
	} else if (title) {
		normalizedTitle = [title];
	}

	// When the showVINAboveTitle pref is on and the vehicle has a VIN, the VIN is
	// rendered on its own line above the title (US2001691). This is independent of
	// the supplemental title (configured in the display config) and works the same
	// whether or not a supplemental title is present.
	const showVinAboveTitle = showVINAboveTitle === 'true' && Boolean(vin);

	// Filter supplementalTitle:
	// - exclude normalFuelType when value is not valid (e.g. "Gasoline")
	// - exclude odometer when value contains no digits
	const filteredSupplementalTitle =
		supplementalTitle?.filter((attr) => {
			if (attr.name === 'normalFuelType')
				return isValidFuelType(attr.value);
			if (attr.name === 'odometer') return /\d/.test(attr.value);
			return true;
		}) || [];

	const formatTitle = ([subtitle, maintitle]) => {
		// TODO: remove subtitle and maintitle reference once line break changes removed from WIS.
		const vehicleTitleSplit =
			maintitle === undefined ? subtitle : `${subtitle} ${maintitle}`;
		const formattedTitle = (
			<React.Fragment>
				<span
					// eslint-disable-next-line react/no-danger
					dangerouslySetInnerHTML={{
						__html: vehicleTitleSplit
					}}
				/>
			</React.Fragment>
		);

		return formattedTitle;
	};

	const showPriceToggle = !showInformationTabs
		?.toLowerCase()
		.includes('pricing');

	const priceToggleElement = (
		<ConditionalWrapper
			condition={!isListLayout || false}
			wrapper={(children) => (
				<Collapse in={!showExpandedPricing}>
					<div>{children}</div>
				</Collapse>
			)}
		>
			<>
				<PriceToggle
					condition={condition}
					pricing={componentPricing}
					isGridLayout={isGridLayout}
					account={accountInfo}
					isDisplayingEPrice={isDisplayingEPrice}
					isNewCarBoost={showNewCarBoostPaymentsInTitle}
					finalPriceClassName={finalPriceSize}
					onToggle={() =>
						setShowExpandedPricing(!showExpandedPricing)
					}
				/>
				{!showNewCarBoostPaymentsInTitle && (
					<WebIntegrationPlaceholder
						location="vehicle-payments-primary"
						classNames={['float-left', 'ddc-font-size-xsmall']}
					/>
				)}
			</>
		</ConditionalWrapper>
	);

	const conditionClassName = condition
		? `inv-type-${condition}`.toLowerCase().replace(/ /g, '-')
		: '';

	return (
		<h2
			data-style-editor-price={isTabbedLayout}
			className={setClassNames([
				'vehicle-card-title',
				'mt-0',
				!isListLayout ? 'd-block mb-0' : 'd-flex',
				'justify-content-between',
				'align-items-end',
				!isListLayout ? vehicleTitleFontSize : null,
				conditionClassName
			])}
		>
			<div className="vehicle-card-title-container">
				{showVinAboveTitle && (
					<span className="vehicle-card-vin-above-title d-block ddc-font-size-xsmall font-weight-normal mb-2">
						{/* Uses the global "VIN" label; labels.get falls back to the key 'VIN' if it is not registered. */}
						{`${labels.get('VIN')} ${vin}`}
					</span>
				)}
				{filteredSupplementalTitle.length > 0 && (
					<span className="vehicle-card-supplemental-title d-block ddc-font-size-xsmall font-weight-normal mb-2">
						{filteredSupplementalTitle.map((attr, index) => {
							const useLabeledValue =
								attr.name === 'vin' ||
								attr.name === 'stockNumber';
							let displayValue = useLabeledValue
								? attr.labeledValue
								: attr.value;
							if (attr.name === 'vehicleStatus') {
								displayValue = titleCaseIfAllCaps(attr.value);
							}
							// For certified vehicles, show "Certified" in place of
							// the "Used" type text (US2001718). The "type" attribute
							// carries new/used (value "used"); the separate "condition"
							// attribute is "Certified Pre-Owned" and is left untouched.
							if (
								certified &&
								attr.name === 'type' &&
								attr.value?.toLowerCase() === 'used'
							) {
								displayValue = labels.get('CERTIFIED');
							}
							return (
								<React.Fragment key={attr.name}>
									{index > 0 && (
										<span
											className="supplemental-title-separator"
											aria-hidden="true"
										>
											{' | '}
										</span>
									)}
									<span className="supplemental-title-attribute">
										{displayValue}
									</span>
								</React.Fragment>
							);
						})}
					</span>
				)}
				{normalizedTitle.length > 0 && (
					<a
						href={link}
						onClick={() => {
							trackEvent(widgetName, windowId, {
								element: 'Vehicle title',
								result: 'Navigated to VDP',
								vehicle: getVehicleObj(vehicleId)
							});
							publishVehicleData(vehicleId);
						}}
					>
						{formatTitle(normalizedTitle)}
					</a>
				)}
			</div>
			{showNewCarBoostPaymentsInTitle && (
				<WebIntegrationPlaceholder
					location="vehicle-payments-primary"
					classNames={['ddc-font-size-xsmall', 'mt-3']}
				/>
			)}
			{!isListLayout && showPriceToggle && priceToggleElement}
		</h2>
	);
};

VehicleTitle.propTypes = {
	pricing: PropTypes.shape({
		ePrice: PropTypes.arrayOf(PropTypes.shape({})),
		dPrice: PropTypes.arrayOf(PropTypes.shape({}))
	}).isRequired,
	accountInfo: PropTypes.shape({}).isRequired,
	title: PropTypes.oneOfType([
		PropTypes.arrayOf(PropTypes.string),
		PropTypes.string
	]),
	supplementalTitle: supplementalTitleShape,
	link: PropTypes.string.isRequired,
	vehicleId: PropTypes.string.isRequired,
	vin: PropTypes.string,
	condition: PropTypes.string.isRequired,
	certified: PropTypes.bool,
	isDisplayingEPrice: PropTypes.bool.isRequired,
	showExpandedPricing: PropTypes.bool.isRequired,
	setShowExpandedPricing: PropTypes.func.isRequired,
	showNewCarBoostPaymentsInTitle: PropTypes.bool
};

export default VehicleTitle;
